if (!customElements.get("product-card-media-carousel")) {
  class ProductCardMediaCarousel extends HTMLElement {
    constructor() {
      super();
      this.activeIndex = 0;
      this.frame = null;
      this.pointerStart = null;
      this.didSwipe = false;
      this.handleScroll = this.handleScroll.bind(this);
      this.handlePrevious = this.handlePrevious.bind(this);
      this.handleNext = this.handleNext.bind(this);
      this.handleMouseEnter = this.handleMouseEnter.bind(this);
      this.handleMouseLeave = this.handleMouseLeave.bind(this);
      this.handlePointerDown = this.handlePointerDown.bind(this);
      this.handlePointerMove = this.handlePointerMove.bind(this);
      this.handlePointerEnd = this.handlePointerEnd.bind(this);
      this.handleClickCapture = this.handleClickCapture.bind(this);
    }

    connectedCallback() {
      if (this.initialized) return;

      this.viewport = this.querySelector("[data-media-carousel-viewport]");
      this.slides = Array.from(this.querySelectorAll("[data-media-carousel-slide]"));
      this.dots = Array.from(this.querySelectorAll("[data-media-carousel-dot]"));
      this.links = Array.from(this.querySelectorAll("[data-media-carousel-link]"));
      this.previousButton = this.querySelector("[data-media-carousel-prev]");
      this.nextButton = this.querySelector("[data-media-carousel-next]");

      if (!this.viewport || this.slides.length < 2 || !this.previousButton || !this.nextButton) return;

      this.initialized = true;
      this.viewport.addEventListener("scroll", this.handleScroll, { passive: true });
      this.viewport.addEventListener("pointerdown", this.handlePointerDown, { passive: true });
      this.viewport.addEventListener("pointermove", this.handlePointerMove, { passive: true });
      this.viewport.addEventListener("pointerup", this.handlePointerEnd, { passive: true });
      this.viewport.addEventListener("pointercancel", this.handlePointerEnd, { passive: true });
      this.previousButton.addEventListener("click", this.handlePrevious);
      this.nextButton.addEventListener("click", this.handleNext);
      this.addEventListener("click", this.handleClickCapture, true);
      this.hoverMediaQuery = window.matchMedia("(hover: hover) and (pointer: fine)");
      if (this.hoverMediaQuery.matches) {
        this.addEventListener("mouseenter", this.handleMouseEnter);
        this.addEventListener("mouseleave", this.handleMouseLeave);
      }
      this.updateControls();
    }

    disconnectedCallback() {
      if (!this.initialized) return;

      this.viewport.removeEventListener("scroll", this.handleScroll);
      this.viewport.removeEventListener("pointerdown", this.handlePointerDown);
      this.viewport.removeEventListener("pointermove", this.handlePointerMove);
      this.viewport.removeEventListener("pointerup", this.handlePointerEnd);
      this.viewport.removeEventListener("pointercancel", this.handlePointerEnd);
      this.previousButton.removeEventListener("click", this.handlePrevious);
      this.nextButton.removeEventListener("click", this.handleNext);
      this.removeEventListener("click", this.handleClickCapture, true);
      if (this.hoverMediaQuery?.matches) {
        this.removeEventListener("mouseenter", this.handleMouseEnter);
        this.removeEventListener("mouseleave", this.handleMouseLeave);
      }

      if (this.frame) cancelAnimationFrame(this.frame);
      this.initialized = false;
    }

    handleScroll() {
      if (this.pointerStart && Math.abs(this.viewport.scrollLeft - this.pointerStart.scrollLeft) > 8) {
        this.didSwipe = true;
      }

      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.updateActiveIndex();
      });
    }

    handlePrevious(event) {
      event.preventDefault();
      event.stopPropagation();
      this.hoverPreviewActive = false;
      this.goTo(this.activeIndex - 1);
    }

    handleNext(event) {
      event.preventDefault();
      event.stopPropagation();
      this.hoverPreviewActive = false;
      this.goTo(this.activeIndex + 1);
    }

    handleMouseEnter() {
      if (this.activeIndex !== 0) return;

      this.hoverPreviewActive = true;
      this.showHoverPreview(1);
    }

    handleMouseLeave() {
      if (!this.hoverPreviewActive) return;

      this.hoverPreviewActive = false;
      this.showHoverPreview(0);
    }

    showHoverPreview(index) {
      this.viewport.scrollTo({
        left: this.slides[index].offsetLeft,
        behavior: "auto",
      });
      this.activeIndex = index;
      this.updateControls();
    }

    handlePointerDown(event) {
      this.didSwipe = false;
      if (event.pointerType === "mouse") return;

      this.pointerStart = {
        x: event.clientX,
        y: event.clientY,
        scrollLeft: this.viewport.scrollLeft,
      };
    }

    handlePointerMove(event) {
      if (!this.pointerStart) return;

      const horizontalDistance = Math.abs(event.clientX - this.pointerStart.x);
      const verticalDistance = Math.abs(event.clientY - this.pointerStart.y);
      if (horizontalDistance > 8 && horizontalDistance > verticalDistance) {
        this.didSwipe = true;
      }
    }

    handlePointerEnd() {
      this.pointerStart = null;
    }

    handleClickCapture(event) {
      if (!this.didSwipe || !event.target.closest("[data-media-carousel-link]")) return;

      event.preventDefault();
      event.stopPropagation();
      this.didSwipe = false;
    }

    goTo(index) {
      const nextIndex = Math.max(0, Math.min(index, this.slides.length - 1));
      const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

      this.viewport.scrollTo({
        left: this.slides[nextIndex].offsetLeft,
        behavior: reduceMotion ? "auto" : "smooth",
      });
    }

    updateActiveIndex() {
      let closestIndex = 0;
      let closestDistance = Number.POSITIVE_INFINITY;

      this.slides.forEach((slide, index) => {
        const distance = Math.abs(slide.offsetLeft - this.viewport.scrollLeft);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      if (closestIndex === this.activeIndex) return;
      this.activeIndex = closestIndex;
      this.updateControls();
    }

    updateControls() {
      this.dots.forEach((dot, index) => {
        dot.classList.toggle("is-active", index === this.activeIndex);
      });

      this.links.forEach((link, index) => {
        link.tabIndex = index === this.activeIndex ? 0 : -1;
        link.setAttribute("aria-hidden", String(index !== this.activeIndex));
      });

      this.setButtonState(this.previousButton, this.activeIndex === 0);
      this.setButtonState(this.nextButton, this.activeIndex === this.slides.length - 1);
    }

    setButtonState(button, disabled) {
      button.disabled = disabled;
      button.setAttribute("aria-disabled", String(disabled));
    }
  }

  customElements.define("product-card-media-carousel", ProductCardMediaCarousel);
}
